import { useState, useEffect } from 'react';
import { Table, Card, Tag, Space, Input, Select, Button, Modal, Avatar, message, Popconfirm } from 'antd';
import {
  SearchOutlined,
  WalletOutlined,
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';

interface Withdrawal {
  id: number;
  driverId: number;
  driver: {
    id: number;
    fullName: string | null;
    phone: string;
    photoUrl: string | null;
  };
  amount: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  bankAccount?: string;
  cardNumber?: string;
  rejectReason?: string;
  createdAt: string;
  processedAt?: string;
}

const statusLabels: Record<string, string> = {
  PENDING: 'Kutilmoqda',
  APPROVED: 'Tasdiqlangan',
  REJECTED: 'Rad etilgan',
};

const statusColors: Record<string, string> = {
  PENDING: 'orange',
  APPROVED: 'green',
  REJECTED: 'red',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
};

export const WithdrawalList = () => {
  const { can } = usePermission();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const fetchWithdrawals = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/withdrawals', {
        params: {
          search: search || undefined,
          status: statusFilter || undefined,
          page,
          limit: pageSize,
        },
      });
      setWithdrawals(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch {
      setWithdrawals([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [page, pageSize]);

  const handleApprove = async (id: number) => {
    try {
      await apiClient.put(`/admin/withdrawals/${id}/approve`);
      message.success('So\'rov tasdiqlandi');
      fetchWithdrawals();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.error('Sabab kiritilishi shart');
      return;
    }

    setRejecting(true);
    try {
      await apiClient.put(`/admin/withdrawals/${rejectingId}/reject`, {
        reason: rejectReason,
      });
      message.success('So\'rov rad etildi');
      setRejectModalOpen(false);
      setRejectingId(null);
      setRejectReason('');
      fetchWithdrawals();
    } catch {
      message.error('Xatolik yuz berdi');
    } finally {
      setRejecting(false);
    }
  };

  const openRejectModal = (id: number) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Haydovchi',
      key: 'driver',
      render: (_: unknown, record: Withdrawal) => (
        <Space>
          <Avatar src={record.driver?.photoUrl} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>{record.driver?.fullName || 'Ismi yo\'q'}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.driver?.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Summa',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(amount)}</span>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Bank / Karta',
      key: 'bankInfo',
      render: (_: unknown, record: Withdrawal) => (
        <div>
          {record.cardNumber && (
            <div style={{ fontFamily: 'monospace' }}>{record.cardNumber}</div>
          )}
          {record.bankAccount && (
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.bankAccount}</div>
          )}
          {!record.cardNumber && !record.bankAccount && (
            <span style={{ color: '#7F8C8D' }}>-</span>
          )}
        </div>
      ),
    },
    {
      title: 'So\'rov sanasi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 160,
      render: (_: unknown, record: Withdrawal) => {
        if (record.status !== 'PENDING') {
          if (record.status === 'REJECTED' && record.rejectReason) {
            return (
              <span style={{ color: '#7F8C8D', fontSize: 12 }}>
                Sabab: {record.rejectReason}
              </span>
            );
          }
          return <span style={{ color: '#7F8C8D' }}>-</span>;
        }

        if (!can('payments', 'update')) return null;
        return (
          <Space>
            <Popconfirm
              title="So'rovni tasdiqlaysizmi?"
              description={`${formatCurrency(record.amount)} yechib olish`}
              onConfirm={() => handleApprove(record.id)}
              okText="Tasdiqlash"
              cancelText="Bekor qilish"
            >
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
              >
                Tasdiqlash
              </Button>
            </Popconfirm>
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={() => openRejectModal(record.id)}
            >
              Rad etish
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <WalletOutlined style={{ marginRight: 8 }} />
        Pul yechish so'rovlari
      </h2>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Qidirish (telefon, ism)"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
            allowClear
            onPressEnter={fetchWithdrawals}
          />
          <Select
            placeholder="Holat"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            allowClear
            options={[
              { value: 'PENDING', label: 'Kutilmoqda' },
              { value: 'APPROVED', label: 'Tasdiqlangan' },
              { value: 'REJECTED', label: 'Rad etilgan' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchWithdrawals}>
            Qidirish
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={withdrawals}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
        />
      </Card>

      {/* Reject Modal */}
      <Modal
        title="So'rovni rad etish"
        open={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectingId(null);
          setRejectReason('');
        }}
        onOk={handleReject}
        okText="Rad etish"
        cancelText="Bekor qilish"
        okButtonProps={{ danger: true, loading: rejecting }}
      >
        <p style={{ marginBottom: 16 }}>
          Rad etish sababini kiriting:
        </p>
        <Input.TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Rad etish sababi..."
          rows={4}
        />
      </Modal>
    </div>
  );
};
